import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

/**
 * PageTransition v2 — cinematic film-title reveal.
 * Black slab enters from top, "Bespoke." glyph pulses in champagne gold,
 * slab exits downward revealing the new page with a subtle scale settle.
 * Feels like a Rolls-Royce or Rimowa product-page transition.
 */
export default function PageTransition({ children }) {
  const location = useLocation();
  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {children}
        </motion.div>
      </AnimatePresence>

      {/* Cinematic curtain */}
      <AnimatePresence>
        <motion.div
          key={`curtain-${location.pathname}`}
          className="fixed inset-0 z-[10000] pointer-events-none flex items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          exit={{ opacity: 1 }}
          transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
        >
          {/* Black slab */}
          <motion.div
            className="absolute inset-0"
            initial={{ y: "0%" }}
            animate={{ y: "-100%" }}
            transition={{ duration: 1.0, ease: [0.85, 0, 0.15, 1], delay: 0.15 }}
            style={{ background: "#050506" }}
          />
          {/* Second slab for depth */}
          <motion.div
            className="absolute inset-0"
            initial={{ y: "0%" }}
            animate={{ y: "100%" }}
            transition={{ duration: 1.0, ease: [0.85, 0, 0.15, 1], delay: 0.2 }}
            style={{
              background: "linear-gradient(180deg, #08080a 0%, #050506 100%)",
              borderTop: "1px solid rgba(198,168,124,0.35)",
            }}
          />
          {/* Champagne pulse title */}
          <motion.div
            className="relative flex flex-col items-center gap-4"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.94, 1, 1, 1.02] }}
            transition={{ duration: 1.1, times: [0, 0.35, 0.65, 1], ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.5em]" style={{ color: "var(--gold)" }}>
              Bespoke
            </div>
            <div className="w-16 h-px" style={{ background: "var(--gold)" }} />
            <div className="font-display-italic text-[clamp(1.8rem, 4vw, 3.2rem)]" style={{ color: "var(--gold-2)", letterSpacing: "-0.02em" }}>
              Exterior Solutions
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}
